import { Breadcrumb, Layout, Spin, Alert, Tabs } from "antd"
import { AiAgentsBoundary } from "./boundary"
import { LayoutPageContent, LayoutPageHeader } from "components"
import { useConfigurationContext } from "contexts/configuration/context"
import { useStellaCustomToken } from "contexts/authorization/hooks/use-stella-custom-token"
import { useState, useEffect, useCallback } from "react"
import { RobotOutlined } from "@ant-design/icons"
import {
  EOpusPermission,
  useOpusPermissions
} from "contexts/authorization/hooks/use-opus-permissions"

export const AiAgents: React.FC = () => {
  const configuration = useConfigurationContext()
  const { customToken, isLoading, error } = useStellaCustomToken()
  const [activeTab, setActiveTab] = useState<string>("policy-docs")

  // Track iframe loading states for each agent
  const [iframeLoadingStates, setIframeLoadingStates] = useState<{
    [key: string]: boolean
  }>({
    "policy-docs": true,
    "data-explorer": true
  })

  const { havePermission: canViewPolicyDataAgent } = useOpusPermissions(
    EOpusPermission.AiToolsPolicyData,
    "true"
  )

  // Handle messages from Stella AI iframes
  const handleIframeMessage = useCallback((event: MessageEvent) => {
    // Validate message structure
    if (event.data?.type === "STELLA_AI_READY") {
      const agent = event.data.agent

      // Map agent names to tab keys
      const agentToTabKey: { [key: string]: string } = {
        policyAgent: "policy-docs",
        snowflakeQueryAgent: "data-explorer"
      }

      const tabKey = agentToTabKey[agent]
      if (tabKey) {
        setIframeLoadingStates((prev) => ({
          ...prev,
          [tabKey]: false
        }))
      }
    }
  }, [])

  // Set up postMessage listener
  useEffect(() => {
    window.addEventListener("message", handleIframeMessage)

    // Set a timeout fallback in case the iframe doesn't send the ready message
    const timeoutIds = [
      setTimeout(() => {
        setIframeLoadingStates((prev) => ({
          ...prev,
          "policy-docs": false
        }))
      }, 10000), // 10 second timeout for policy-docs
      setTimeout(() => {
        setIframeLoadingStates((prev) => ({
          ...prev,
          "data-explorer": false
        }))
      }, 10000) // 10 second timeout for data-explorer
    ]

    return () => {
      window.removeEventListener("message", handleIframeMessage)
      timeoutIds.forEach(clearTimeout)
    }
  }, [handleIframeMessage])

  if (isLoading) {
    return (
      <AiAgentsBoundary>
        <Layout
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "82vh"
          }}
        >
          <Spin size="large" tip="Loading AI Agents..." />
        </Layout>
      </AiAgentsBoundary>
    )
  }

  if (error) {
    return (
      <AiAgentsBoundary>
        <Layout style={{ padding: "20px" }}>
          <Alert
            message="Error Loading AI Agents"
            description={`Failed to authenticate with Stella AI Agent: ${error}`}
            type="error"
            showIcon
          />
        </Layout>
      </AiAgentsBoundary>
    )
  }

  const stellaBaseUrl = configuration.aiPolicyAssistant.url

  if (!stellaBaseUrl) {
    return (
      <AiAgentsBoundary>
        <Layout style={{ padding: "20px" }}>
          <Alert
            message="Configuration Error"
            description="Stella AI Agent base URL is not configured. Please add 'url' to your aiPolicyAssistant configuration."
            type="error"
            showIcon
          />
        </Layout>
      </AiAgentsBoundary>
    )
  }

  // Build iframe URLs with custom token
  const policyDocsUrl = customToken
    ? `${stellaBaseUrl}&agent=policyAgent&customToken=${encodeURIComponent(
        customToken
      )}`
    : `${stellaBaseUrl}&agent=policyAgent`

  const dataExplorerUrl = customToken
    ? `${stellaBaseUrl}&agent=snowflakeQueryAgent&customToken=${encodeURIComponent(
        customToken
      )}`
    : `${stellaBaseUrl}&agent=snowflakeQueryAgent`

  const onTabClick = (activeKey: string) => {
    setActiveTab(activeKey)
  }

  return (
    <AiAgentsBoundary>
      <Layout>
        <LayoutPageHeader
          ghost={false}
          breadcrumb={
            <Breadcrumb>
              <Breadcrumb.Item key="/ai-agents">
                <RobotOutlined />
                &nbsp;AI Agents
              </Breadcrumb.Item>
            </Breadcrumb>
          }
          title="AI Agents"
          footer={
            <Tabs
              onTabClick={onTabClick}
              defaultActiveKey="policy-docs"
              activeKey={activeTab}
            >
              <Tabs.TabPane
                tab="Policy Document Queries"
                key="policy-docs"
              ></Tabs.TabPane>
              {canViewPolicyDataAgent && (
                <Tabs.TabPane
                  tab="Policy Data Explorer"
                  key="data-explorer"
                ></Tabs.TabPane>
              )}
            </Tabs>
          }
        />
        <LayoutPageContent transparent>
          <div style={{ position: "relative" }}>
            <iframe
              title="AI Policy Doc Assistant"
              src={policyDocsUrl}
              allow="clipboard-write"
              style={{
                display: activeTab === "policy-docs" ? "block" : "none",
                width: "100%",
                height: "calc(100vh - 250px)",
                border: "none",
                outline: "none"
              }}
            />
            {iframeLoadingStates["policy-docs"] && activeTab === "policy-docs" && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  zIndex: 1000
                }}
              >
                <Spin size="large" tip="Loading AI Agent..." />
              </div>
            )}
          </div>
          {canViewPolicyDataAgent && (
            <div style={{ position: "relative" }}>
              <iframe
                title="AI Policy Query Assistant"
                src={dataExplorerUrl}
                allow="clipboard-write"
                style={{
                  display: activeTab === "data-explorer" ? "block" : "none",
                  width: "100%",
                  height: "calc(100vh - 250px)",
                  border: "none",
                  outline: "none"
                }}
              />
              {iframeLoadingStates["data-explorer"] &&
                activeTab === "data-explorer" && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      zIndex: 1000
                    }}
                  >
                    <Spin size="large" tip="Loading AI Agent..." />
                  </div>
                )}
            </div>
          )}
        </LayoutPageContent>
      </Layout>
    </AiAgentsBoundary>
  )
}
